import type { Note, AINote, Person, Project, Task, Edge, GraphData, User, Mood, Body, Food, Activity, Resource, Reflection } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('neron_token', token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('neron_token');
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('neron_token');
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        window.location.reload();
      }
      throw new Error(`API error: ${response.status}`);
    }

    return response.json() as Promise<T>;
  }

  // Auth - Token validation
  async validateToken(token: string): Promise<{ valid: boolean; user?: User; error?: string }> {
    return this.request('/auth/validate', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // Auth - Password login
  async loginWithPassword(telegramId: number, password: string): Promise<{ success: boolean; token?: string; user?: User; error?: string }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ telegram_id: telegramId, password }),
    });
  }

  // AI Chat
  async aiChat(question: string): Promise<{ response?: string; answer?: string; error?: string; usage?: any }> {
    return this.request('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  }

  // Graph
  async getGraph(): Promise<GraphData> {
    return this.request('/graph');
  }

  // Notes
  async getNotes(limit?: number): Promise<{ notes: Note[] }> {
    const query = limit ? `?limit=${limit}` : '';
    return this.request(`/notes${query}`);
  }

  async searchNotes(query: string): Promise<{ notes: Note[] }> {
    return this.request(`/notes/search?q=${encodeURIComponent(query)}`);
  }

  async createNote(text: string): Promise<{ note: Note }> {
    return this.request('/notes', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async deleteNote(id: number): Promise<{ success: boolean }> {
    return this.request(`/notes/${id}`, { method: 'DELETE' });
  }

  // People
  async getPeople(): Promise<{ people: Person[] }> {
    return this.request('/people');
  }

  async createPerson(person: Partial<Person>): Promise<{ person: Person }> {
    return this.request('/people', {
      method: 'POST',
      body: JSON.stringify(person),
    });
  }

  // Projects
  async getProjects(): Promise<{ projects: Project[] }> {
    return this.request('/projects');
  }

  async createProject(project: Partial<Project>): Promise<{ project: Project }> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    });
  }

  // Tasks
  async getTasks(): Promise<{ tasks: Task[] }> {
    return this.request('/tasks');
  }

  async createTask(task: Partial<Task>): Promise<{ task: Task }> {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    });
  }

  async updateTask(id: number, updates: Partial<Task>): Promise<{ task: Task }> {
    return this.request(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // AI Notes
  async getAINotes(): Promise<{ ai_notes: AINote[] }> {
    return this.request('/graph/ai-notes');
  }

  // Edges
  async getEdges(): Promise<{ edges: Edge[] }> {
    return this.request('/graph/edges');
  }

  async createEdge(edge: { from_type: string; from_id: number; to_type: string; to_id: number; relationship: string }): Promise<{ edge: any }> {
    return this.request('/graph/edges', {
      method: 'POST',
      body: JSON.stringify(edge),
    });
  }

  // Generic entity delete
  async deleteEntity(type: string, id: number): Promise<{ success: boolean }> {
    return this.request(`/graph/entity/${type}/${id}`, { method: 'DELETE' });
  }

  // Entity updates
  async updatePerson(id: number, updates: Partial<Person>): Promise<{ person: Person }> {
    return this.request(`/people/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateNote(id: number, updates: Partial<Note>): Promise<{ note: Note }> {
    return this.request(`/notes/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateProject(id: number, updates: Partial<Project>): Promise<{ project: Project }> {
    return this.request(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateMood(id: number, updates: Partial<Mood>): Promise<{ mood: Mood }> {
    return this.request(`/extractions/moods/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateBody(id: number, updates: Partial<Body>): Promise<{ body: Body }> {
    return this.request(`/extractions/bodies/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateFood(id: number, updates: Partial<Food>): Promise<{ food: Food }> {
    return this.request(`/extractions/foods/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateActivity(id: number, updates: Partial<Activity>): Promise<{ activity: Activity }> {
    return this.request(`/extractions/activities/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateResource(id: number, updates: Partial<Resource>): Promise<{ resource: Resource }> {
    return this.request(`/extractions/resources/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  async updateReflection(id: number, updates: Partial<Reflection>): Promise<{ reflection: Reflection }> {
    return this.request(`/extractions/reflections/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
  }

  // Edge management
  async deleteEdge(id: number): Promise<{ success: boolean }> {
    return this.request(`/graph/edges/${id}`, { method: 'DELETE' });
  }

  // Extractions
  async getMoods(): Promise<{ moods: Mood[] }> { return this.request('/extractions/moods'); }
  async getBodies(): Promise<{ bodies: Body[] }> { return this.request('/extractions/bodies'); }
  async getFoods(): Promise<{ foods: Food[] }> { return this.request('/extractions/foods'); }
  async getActivities(): Promise<{ activities: Activity[] }> { return this.request('/extractions/activities'); }
  async getResources(): Promise<{ resources: Resource[] }> { return this.request('/extractions/resources'); }
  async getReflections(): Promise<{ reflections: Reflection[] }> { return this.request('/extractions/reflections'); }
}

export const api = new ApiClient();
